import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

/**
 * JSON Config Editor
 * - Syntax highlighting
 * - Validation
 * - Pretty formatting
 */
const JSONConfigEditor = ({ value, onChange }) => {
  const [jsonString, setJsonString] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Sync editor with incoming value whenever it changes
    try {
      const formatted = JSON.stringify(value ?? {}, null, 2);
      setJsonString(formatted);
      setIsValid(true);
      setErrorMessage('');
    } catch (error) {
      setJsonString('{}');
      setIsValid(false);
      setErrorMessage('Invalid JSON');
    }
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setJsonString(newValue);

    // Validate JSON
    try {
      const parsed = JSON.parse(newValue);
      setIsValid(true);
      setErrorMessage('');
      onChange(parsed);
    } catch (error) {
      setIsValid(false);
      setErrorMessage(error.message);
    }
  };

  const formatJSON = () => {
    try {
      const parsed = JSON.parse(jsonString);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonString(formatted);
      setIsValid(true);
      setErrorMessage('');
      onChange(parsed);
    } catch (error) {
      // Already showing error, no need to do anything
    }
  };

  return (
    <div>
      <div className="relative">
        <textarea
          value={jsonString}
          onChange={handleChange}
          className={`
            w-full px-4 py-3 font-mono text-sm rounded-lg border-2
            focus:outline-none focus:ring-2 focus:ring-cyan-500
            dark:bg-spooky-bg-medium dark:text-spooky-ghost-white
            ${
              isValid
                ? 'border-gray-300 dark:border-gray-600'
                : 'border-red-300 dark:border-red-600'
            }
          `}
          rows={12}
          spellCheck={false}
        />
        <div className="absolute top-2 right-2">
          {isValid ? (
            <FaCheckCircle className="text-green-500" title="Valid JSON" />
          ) : (
            <FaExclamationCircle className="text-red-500" title="Invalid JSON" />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <div>
          {isValid ? (
            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <FaCheckCircle />
              Valid JSON
            </span>
          ) : (
            <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
              <FaExclamationCircle />
              {errorMessage}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={formatJSON}
          disabled={!isValid}
          className="text-xs px-3 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Format
        </button>
      </div>
    </div>
  );
};

export default JSONConfigEditor;
